// Synth verb — bake a synth hit to a deterministic buffer.
//
// Audition is live (uses the shared AudioContext directly). Commit uses
// OfflineAudioContext so the captured buffer is bit-stable regardless
// of timing jitter or load.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SynthRecipe } from '../../types';
import type { VerbProps } from '../types';
import {
  DEFAULT_SYNTH_RECIPE,
  buildSynthGraph,
  midiToHz,
  renderSynth,
  rollRecipe,
  type ToneClass,
} from './synthEngine';

const OSC_OPTIONS: SynthRecipe['osc'][] = [
  'sine',
  'sawtooth',
  'square',
  'triangle',
];

const TONE_CLASSES: ToneClass[] = ['kick', 'snare', 'perc', 'bass', 'lead'];

const MIDI_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
function midiName(m: number): string {
  const oct = Math.floor(m / 12) - 1;
  return MIDI_NAMES[m % 12]! + String(oct);
}

export function SynthVerb({ audioContext, onBuffer, onStatus }: VerbProps) {
  const [recipe, setRecipe] = useState<SynthRecipe>(DEFAULT_SYNTH_RECIPE);
  const [toneClass, setToneClass] = useState<ToneClass>('kick');
  const liveNodesRef = useRef<AudioNode[]>([]);

  const stopLive = useCallback(() => {
    for (const n of liveNodesRef.current) {
      try {
        n.disconnect();
      } catch {
        // already disconnected
      }
    }
    liveNodesRef.current = [];
  }, []);

  useEffect(() => () => stopLive(), [stopLive]);

  const audition = useCallback(() => {
    if (!audioContext) return;
    stopLive();
    // Build a fresh graph directly on the live ctx. The nodes finish
    // themselves when the oscillator's stop() fires.
    buildSynthGraph(audioContext, recipe, audioContext.destination);
  }, [audioContext, recipe, stopLive]);

  const handleRender = useCallback(async () => {
    if (!audioContext) {
      onStatus('engine not booted — press Play first', 'warn');
      return;
    }
    try {
      const buffer = await renderSynth(recipe, audioContext.sampleRate);
      onBuffer(buffer, {
        suggestedName: `synth_${toneClass}`,
        origin: { kind: 'synth', recipe: { ...recipe } },
        pitchedRootMidi: recipe.pitchMidi,
      });
      onStatus(`Rendered ${toneClass} — trim and save.`, 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    }
  }, [audioContext, recipe, toneClass, onBuffer, onStatus]);

  const handleRoll = useCallback(() => {
    setRecipe(rollRecipe(toneClass));
  }, [toneClass]);

  const patch = (diff: Partial<SynthRecipe>) =>
    setRecipe((prev) => ({ ...prev, ...diff }));

  return (
    <div className="flex flex-col gap-4 min-h-[200px]">
      <section>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
          Tone class (constrains Roll)
        </div>
        <div className="flex gap-2 flex-wrap">
          {TONE_CLASSES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setToneClass(c)}
              className={
                'px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] rounded-sm border ' +
                (toneClass === c
                  ? 'bg-yellow-300 border-yellow-300 text-zinc-950 font-semibold'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800')
              }
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-y-3 text-xs">
        <label className="flex items-center gap-2">
          <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-24">Osc</span>
          <select
            value={recipe.osc}
            onChange={(e) => patch({ osc: e.target.value as SynthRecipe['osc'] })}
            className="bg-zinc-950 border border-zinc-700 px-2 py-1 text-zinc-200"
          >
            {OSC_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <SliderRow
          label="Pitch"
          value={recipe.pitchMidi}
          min={24}
          max={96}
          step={1}
          display={`${midiName(recipe.pitchMidi)}  ${midiToHz(recipe.pitchMidi).toFixed(1)}Hz`}
          onChange={(v) => patch({ pitchMidi: v })}
        />
        <SliderRow
          label="Detune"
          value={recipe.detuneCents}
          min={-100}
          max={100}
          step={1}
          display={`${recipe.detuneCents > 0 ? '+' : ''}${recipe.detuneCents.toFixed(0)}¢`}
          onChange={(v) => patch({ detuneCents: v })}
        />
        <SliderRow
          label="Attack"
          value={recipe.attackMs}
          min={0}
          max={500}
          step={1}
          display={`${recipe.attackMs.toFixed(0)}ms`}
          onChange={(v) => patch({ attackMs: v })}
        />
        <SliderRow
          label="Decay"
          value={recipe.decayMs}
          min={1}
          max={1000}
          step={1}
          display={`${recipe.decayMs.toFixed(0)}ms`}
          onChange={(v) => patch({ decayMs: v })}
        />
        <SliderRow
          label="Sustain"
          value={recipe.sustain}
          min={0}
          max={1}
          step={0.01}
          display={recipe.sustain.toFixed(2)}
          onChange={(v) => patch({ sustain: v })}
        />
        <SliderRow
          label="Release"
          value={recipe.releaseMs}
          min={1}
          max={1000}
          step={1}
          display={`${recipe.releaseMs.toFixed(0)}ms`}
          onChange={(v) => patch({ releaseMs: v })}
        />
        <SliderRow
          label="Filter"
          value={recipe.filterHz}
          min={80}
          max={10000}
          step={10}
          display={`${recipe.filterHz.toFixed(0)}Hz`}
          onChange={(v) => patch({ filterHz: v })}
        />
        <SliderRow
          label="Env amt"
          value={recipe.filterEnvAmount}
          min={0}
          max={1}
          step={0.01}
          display={recipe.filterEnvAmount.toFixed(2)}
          onChange={(v) => patch({ filterEnvAmount: v })}
        />
        <SliderRow
          label="Length"
          value={recipe.lengthMs}
          min={50}
          max={2000}
          step={10}
          display={`${recipe.lengthMs.toFixed(0)}ms`}
          onChange={(v) => patch({ lengthMs: v })}
        />
      </section>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={audition}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700"
        >
          ▶ Audition
        </button>
        <button
          type="button"
          onClick={handleRoll}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700"
        >
          ⚃ Roll
        </button>
        <button
          type="button"
          onClick={() => void handleRender()}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold"
        >
          ● Render
        </button>
      </div>
    </div>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="uppercase tracking-[0.2em] text-[10px] text-zinc-500 w-16 shrink-0">
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="accent-yellow-300 flex-1 min-w-0"
      />
      <span className="tabular-nums w-14 text-right text-zinc-300 text-[11px]">
        {display}
      </span>
    </label>
  );
}
